import { useState } from "react";
import { BookOpen, BookX, ExternalLink, UserCheck, UserX } from "lucide-react";
import { toast } from "sonner";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { OverviewSettingRow } from "@/components/agent/overview/OverviewSettingRow";
import { GenieMemorySheet } from "@/components/agent/details/GenieMemorySheet";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { Agent } from "@/types/agent/agent";

interface ExtraSettingsCardProps {
  agent: Agent;
  brand?: string;
}

export const ExtraSettingsCard = ({ agent, brand = "#4E9CFF" }: ExtraSettingsCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isMemorySheetOpen, setIsMemorySheetOpen] = useState(false);

  const [redirectUrl, setRedirectUrl] = useState(agent.redirect_url ?? "");
  const [captureLeads, setCaptureLeads] = useState(agent.capture_leads ?? true);
  const [memoryEnabled, setMemoryEnabled] = useState(agent.memory_enabled ?? false);

  const updateAgent = useUpdateAgent();

  const handleEdit = () => {
    setRedirectUrl(agent.redirect_url ?? "");
    setCaptureLeads(agent.capture_leads ?? true);
    setMemoryEnabled(agent.memory_enabled ?? false);
    setIsEditing(true);
  };

  const handleCancel = () => setIsEditing(false);

  const handleSave = async () => {
    await updateAgent.mutateAsync({
      agent,
      updateData: {
        redirect_url: redirectUrl || null,
        capture_leads: captureLeads,
        memory_enabled: memoryEnabled,
      },
    });
    toast.success("Settings saved");
    setIsEditing(false);
  };

  return (
    <OverviewSectionCard
      title="Extra"
      subtitle="Post-call redirect, lead capture, and memory"
      genieIcon="sliders"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
      onEdit={isEditing ? undefined : handleEdit}
      onSave={isEditing ? handleSave : undefined}
      onCancel={isEditing ? handleCancel : undefined}
      isSaving={updateAgent.isPending}
    >
      {isEditing ? (
        <div className="space-y-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ExternalLink className="w-4 h-4 text-gray-500" />
              <label className="text-sm font-medium text-gray-700">Redirect After Call</label>
            </div>
            <p className="text-xs text-gray-500 mb-2">URL where users are redirected after the conversation ends</p>
            <input
              type="url"
              value={redirectUrl}
              onChange={(e) => setRedirectUrl(e.target.value)}
              placeholder="https://example.com/thank-you"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              {captureLeads ? <UserCheck className="w-4 h-4 text-gray-500" /> : <UserX className="w-4 h-4 text-gray-500" />}
              <div>
                <p className="text-sm font-medium text-gray-700">Capture Leads</p>
                <p className="text-xs text-gray-500">Automatically create lead records from conversations</p>
              </div>
            </div>
            <Switch checked={captureLeads} onCheckedChange={setCaptureLeads} />
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              {memoryEnabled ? <BookOpen className="w-4 h-4 text-gray-500" /> : <BookX className="w-4 h-4 text-gray-500" />}
              <div>
                <p className="text-sm font-medium text-gray-700">Genie Memory</p>
                <p className="text-xs text-gray-500">Remember opted-in, signed-in customers for 90 days</p>
              </div>
            </div>
            <Switch checked={memoryEnabled} onCheckedChange={setMemoryEnabled} />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <OverviewSettingRow
            label="Redirect URL"
            value={
              agent.redirect_url ? (
                <a href={agent.redirect_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate max-w-[200px] block">
                  {agent.redirect_url}
                </a>
              ) : (
                <span className="text-sm text-gray-400 italic">Not set</span>
              )
            }
          />
          <OverviewSettingRow
            label="Capture Leads"
            value={<Badge variant={agent.capture_leads ? "default" : "secondary"}>{agent.capture_leads ? "Enabled" : "Disabled"}</Badge>}
          />
          <OverviewSettingRow
            label="Genie Memory"
            value={
              <div className="flex items-center gap-3">
                <Badge variant={agent.memory_enabled ? "default" : "secondary"}>{agent.memory_enabled ? "Enabled" : "Disabled"}</Badge>
                {agent.memory_enabled && (
                  <button
                    type="button"
                    onClick={() => setIsMemorySheetOpen(true)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                    style={{ backgroundColor: `${brand}15`, color: brand }}
                  >
                    View memories
                  </button>
                )}
              </div>
            }
          />
        </div>
      )}
      <GenieMemorySheet
        isOpen={isMemorySheetOpen}
        onClose={() => setIsMemorySheetOpen(false)}
        genieId={agent.id}
      />
    </OverviewSectionCard>
  );
};
